import { zodResolver } from '@hookform/resolvers/zod'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, Flex, Modal, sendNotification, Typography } from 'components'
import { SquareSkeleton } from 'components/Skeletons/SquareSkeleton'

import { QRCodeSVG } from 'qrcode.react'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useActivateMfaMutation, useRegisterMFAQuery } from 'services/auth/mfa'
import { enableMFA } from 'services/slices/auth'
import { useTranslation } from 'utils/useTranslation'
import { z } from 'zod'
import { OTP } from './OTP'

export enum MFAError {
  INVALID_MFA_TYPE = 'invalid_mfa_type',
  USER_NOT_FOUND = 'user_not_found',
  UNDEFINED_MFA_TYPE = 'undefined_mfa_type',
  MFA_ALREADY_ACTIVATED = 'mfa_already_activated',
  INVALID_MFA_VALUE = 'invalid_mfa_value',
  INVALID_MFA_STATE = 'invalid_mfa_state',
}

const schema = z.object({
  otp: z.string().min(6),
})

type FormInputs = z.infer<typeof schema>

export const ActivateMFAModal: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('pages.mfa')
  const methods = useForm<FormInputs>({
    resolver: zodResolver(schema),
  })

  const { data: activationUri, isLoading: isActivationUriLoading } = useRegisterMFAQuery(
    isOpen ? undefined : skipToken,
  )
  const dispatch = useDispatch()

  const [activateMFA] = useActivateMfaMutation()

  const onSubmit = (data: FormInputs) => {
    activateMFA({
      type: 'totp',
      value: data.otp,
    })
      .unwrap()
      .then(() => {
        dispatch(enableMFA())
        sendNotification({
          type: 'success',
          // header: 'MFA activated',
          toastContent: t('mfa_activated'),
        })
        close()
      })
      .catch((error) => {
        const errorCode = (error as any)?.data?.error_code
        if (Object.values(MFAError).includes(errorCode as MFAError)) {
          sendNotification({
            type: 'error',
            toastContent: t(`errors.${errorCode}`),
          })
        } else {
          sendNotification({
            type: 'error',
            toastContent: t('errors.something_went_wrong'),
          })
        }
      })
  }

  const close = () => {
    methods.reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      onClose={close}
      heading={t('activate_mfa')}
      noFooter
    >
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex directionColumn="zero" gap={1}>
          <Flex directionColumn="zero" gap={0.25}>
            <Typography fontSize="text_md" fontWeight="semibold" color="grey900">
              {t('step_1')}: {t('scan_the_qr_code')}
            </Typography>
            <Typography fontSize="text_sm" variant="span" color="grey600">
              {t('scan_description')}
            </Typography>
          </Flex>
          <Flex fullWidth center="zero">
            {isActivationUriLoading ? (
              <SquareSkeleton height={200} width={200} borderRadius={12} />
            ) : (
              <QRCodeSVG value={activationUri?.totp_uri ?? ''} size={200} />
              // <QRImage content={activationUri?.totp_uri ?? ''} size={200} />
            )}
          </Flex>

          <Flex directionColumn="zero" gap={0.25}>
            <Typography fontSize="text_md" fontWeight="semibold" color="grey900">
              {t('step_2')}: {t('enter_one_time_password')}
            </Typography>
            <Typography fontSize="text_sm" variant="span" color="grey600">
              {t('enter_description')}
            </Typography>
          </Flex>
          <Flex center="zero" fullWidth>
            <Controller
              control={methods.control}
              name="otp"
              render={({ field: { onChange, value } }) => (
                <OTP disabled={isActivationUriLoading} onChange={onChange} value={value} />
              )}
            />
          </Flex>
          <Button
            type="submit"
            disabled={isActivationUriLoading}
            style={{
              width: '100%',
            }}
          >
            {t('submit_code')}
          </Button>
        </Flex>
      </form>
    </Modal>
  )
}
